import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PERSONA_LABELS, PERSONAS, type IProfile, type Persona } from '@stem/contracts';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';

const PERSONA_BLURBS: Record<Persona, string> = {
  teacher: 'CPD courses, lesson plans, schemes of work and the virtual laboratories.',
  teacher_trainer: 'Training of trainers, facilitation skills and course authoring.',
  school_leader: 'Instructional leadership, school analytics and teacher performance.',
  student: 'Interactive lessons, virtual experiments, practice questions and career guidance.',
  curriculum_officer: 'Curriculum implementation monitoring and coverage against outcomes.',
  partner_admin: 'National dashboards, programme monitoring and impact reporting.',
};

/**
 * "Select their respective role (changeable downstream)."
 *
 * That line in the outline is asking for two things at once, and they have to
 * be separated to be answerable. What a user picks here is a **persona**: it
 * chooses their dashboard and what the catalog recommends, they can change it
 * from settings at any time, and it grants nothing whatsoever.
 *
 * What they cannot pick is a **role**. Roles are granted inside an organisation
 * by whoever administers it, which is why the second half of this page is about
 * joining one — by invitation code, or by an email domain the school has
 * verified — rather than about choosing a title.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const { profile, orgs, setProfile } = useSession();

  const [persona, setPersona] = useState<Persona>(profile?.persona ?? 'teacher');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const joinable = useQuery({
    queryKey: ['join-options'],
    queryFn: () =>
      apiClient.user<Array<{ slug: string; name: string; kind: string; role: string }>>(
        'get',
        '/v1/join/options',
      ),
  });

  const savePersona = useMutation({
    mutationFn: (next: Persona) => apiClient.user<IProfile>('patch', '/v1/profile', { persona: next }),
    onSuccess: updated => setProfile(updated),
  });

  const acceptInvite = useMutation({
    mutationFn: (inviteCode: string) =>
      apiClient.user<{ slug: string }>('post', `/v1/join/invitation/${inviteCode}`),
    onSuccess: org => navigate(`/o/${org.slug}`, { replace: true }),
    onError: caught => setError((caught as Error).message),
  });

  const joinDomain = useMutation({
    mutationFn: (slug: string) => apiClient.user<{ slug: string }>('post', `/v1/join/domain/${slug}`),
    onSuccess: org => navigate(`/o/${org.slug}`, { replace: true }),
    onError: caught => setError((caught as Error).message),
  });

  const personalOrg = orgs.find(org => org.kind === 'personal');

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="text-2xl font-bold text-brand-900">Welcome{profile ? `, ${profile.fullName}` : ''}</h1>
      <p className="mt-1 text-sm text-ink-600">
        Two quick things. Neither is permanent — you can change both later.
      </p>

      <section className="card mt-6">
        <h2 className="text-lg font-semibold text-ink-900">What describes you best?</h2>
        <p className="mb-4 text-sm text-ink-600">
          This chooses your dashboard and what we recommend. It does not decide what you can access —
          that comes from your school and your subscription.
        </p>

        <div className="grid gap-2 sm:grid-cols-2">
          {PERSONAS.map(option => (
            <button
              key={option}
              type="button"
              className={`rounded-lg border p-3 text-left transition-colors ${
                persona === option
                  ? 'border-brand-600 bg-brand-50'
                  : 'border-ink-200 hover:border-ink-300'
              }`}
              onClick={() => {
                setPersona(option);
                savePersona.mutate(option);
              }}
            >
              <span className="block text-sm font-semibold text-ink-900">
                {PERSONA_LABELS[option]}
              </span>
              <span className="mt-0.5 block text-xs text-ink-600">{PERSONA_BLURBS[option]}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="card mt-4">
        <h2 className="text-lg font-semibold text-ink-900">Join your school or institution</h2>
        <p className="mb-4 text-sm text-ink-600">
          Optional. Without one you keep your own personal workspace and the free catalogue.
        </p>

        {joinable.data && joinable.data.length > 0 && (
          <div className="mb-4 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Your email address is recognised by
            </p>
            {joinable.data.map(org => (
              <div
                key={org.slug}
                className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 p-3"
              >
                <div>
                  <p className="text-sm font-medium text-ink-900">{org.name}</p>
                  <p className="text-xs text-ink-500">You would join as {org.role}</p>
                </div>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => joinDomain.mutate(org.slug)}
                >
                  Join
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="label" htmlFor="code">
          Or enter an invitation code
        </label>
        <div className="flex gap-2">
          <input
            id="code"
            className="input"
            value={code}
            onChange={event => setCode(event.target.value.trim())}
            placeholder="teacher-a1b2c3d4"
          />
          <button
            type="button"
            className="btn-primary"
            disabled={!code || acceptInvite.isPending}
            onClick={() => acceptInvite.mutate(code)}
          >
            Join
          </button>
        </div>

        {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
      </section>

      <div className="mt-6 flex justify-between">
        <button
          type="button"
          className="btn-ghost"
          onClick={() => navigate(personalOrg ? `/o/${personalOrg.slug}` : '/switch', { replace: true })}
        >
          Skip for now
        </button>
        <button type="button" className="btn-primary" onClick={() => navigate('/switch')}>
          Continue
        </button>
      </div>
    </div>
  );
}
